import React, {
  RefObject,
} from 'react';
import {
  PCC_PROGRAM_ICONS,
} from 'views/EMR/components/PCC/programs';
import {
  Callout,
} from 'views/EMR/components/PCC/Walkthrough/components/ContentPrimitives';
import PageIntroduction from 'views/EMR/components/PCC/Walkthrough/components/PageIntroduction';
import WalkthroughIcon from 'views/EMR/components/PCC/Walkthrough/icons';
import type {
  ProgramItem,
  ProgramsPageDefinition,
} from 'views/EMR/components/PCC/Walkthrough/types';

type Props = {
  page: ProgramsPageDefinition;
  headingRef: RefObject<HTMLHeadingElement | null>;
};

type ProgramCardProps = {
  program: ProgramItem;
};

function ProgramCard({ program }: ProgramCardProps) {
  const ProgramIcon = PCC_PROGRAM_ICONS[program.programKey];

  return (
    <article
      className="flex flex-col gap-3 rounded-2xl border border-[#E2E8F0] bg-white p-[26px]"
    >
      <div>
        <div className="inline-flex items-center gap-3">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
            <ProgramIcon
              aria-hidden
              className="!h-[22px] !w-[22px]"
            />
          </span>
          <span className="text-sm font-bold uppercase tracking-[0.06em] text-brand-700">
            {program.label}
          </span>
        </div>
      </div>
      <h2 className="m-0 text-[22px] font-bold text-[#0F1B2D] xl:min-h-[53px]">
        {program.title}
      </h2>
      <div className="text-[13px] font-semibold text-brand-500 xl:min-h-[40px]">
        {program.billingText}
      </div>
      <p className="m-0 flex-1 text-[15px] leading-[1.55] text-[#475569]">
        {program.description}
      </p>
      <div className="flex items-start gap-2 text-sm font-semibold text-[#0F1B2D] xl:min-h-[40px]">
        <WalkthroughIcon
          iconKey="calendar"
          className="!h-[18px] !w-[18px] !text-brand-500"
        />
        <span>{program.timing}</span>
      </div>
      <div className="flex flex-col gap-2 border-t border-[#E2E8F0] pt-3.5">
        <div className="text-xs font-bold uppercase tracking-[0.06em] text-[#475569]">
          {program.availabilityLabel}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {program.specialties.map((specialty) => (
            <span
              key={specialty}
              className="rounded-full border border-[#E2E8F0] bg-white px-2.5 py-1 text-[13px] font-semibold text-[#0F1B2D]"
            >
              {specialty}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
}

export default function ProgramsPage({
  page,
  headingRef,
}: Props) {
  return (
    <div className="flex flex-col gap-5">
      <PageIntroduction
        eyebrow={page.introduction.eyebrow}
        heading={page.introduction.heading}
        description={page.introduction.description}
        headingRef={headingRef}
      />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {page.programs.map((program) => (
          <ProgramCard
            key={program.label}
            program={program}
          />
        ))}
      </div>

      <Callout
        text={page.callout.text}
        iconKey={page.callout.iconKey}
      />
    </div>
  );
}
